import { DomainError } from './errors';
import type {
  CreateContainerInput,
  FoodDateEvidenceSet,
  FoodContainer,
  HorizonRuleSnapshot,
  InventoryEvent,
  InventoryEventType,
  InventoryMutation,
  IsoDateTime,
  QuantityUnit,
} from './model';
import {
  calculateVisibleNumberReleaseAt,
  hasVisibleNumber,
  VISIBLE_NUMBER_QUARANTINE,
  type VisibleNumberAssignment,
} from './visible-number';
import { containerReference } from './storage-policy';

export interface MutationContext {
  householdId: string;
  deviceId: string;
  deviceSequence: number;
  eventId: string;
  idempotencyKey: string;
  now: IsoDateTime;
}

interface PartialConsumption {
  mode: 'remaining' | 'used';
  value: number;
  unit: QuantityUnit;
  replacementLocationId: string;
}

const roundQuantity = (value: number): number => Math.round((value + Number.EPSILON) * 1_000) / 1_000;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const assertOptionalDate = (value: string | null | undefined, label: string): void => {
  if (value == null) return;
  if (!ISO_DATE_PATTERN.test(value) || Number.isNaN(Date.parse(`${value}T12:00:00.000Z`))) {
    throw new DomainError(`${label} est invalide.`, 'INVALID_STATE');
  }
};

const assertFridgeDates = (
  trackingMode: FoodContainer['trackingMode'],
  dates: Pick<FoodContainer, 'purchasedOn' | 'openedOn' | 'useFirstOn'>,
): void => {
  assertOptionalDate(dates.purchasedOn, 'La date d’achat');
  assertOptionalDate(dates.openedOn, 'La date d’ouverture');
  assertOptionalDate(dates.useFirstOn, 'Le repère choisi');
  if (
    trackingMode === 'visible-number' &&
    [dates.purchasedOn, dates.openedOn, dates.useFirstOn].some((value) => value != null)
  ) {
    throw new DomainError(
      'Les dates d’achat, d’ouverture et le repère choisi sont réservés au mode frigo.',
      'INVALID_STATE',
    );
  }
  if (dates.purchasedOn != null && dates.openedOn != null && dates.openedOn < dates.purchasedOn) {
    throw new DomainError(
      'La date d’ouverture ne peut pas précéder la date d’achat.',
      'INVALID_STATE',
    );
  }
};

const normalizeDateEvidence = (
  dates: Pick<FoodContainer, 'storageStartedOn' | 'purchasedOn' | 'openedOn' | 'useFirstOn' | 'manufacturerDate'>,
  evidence?: FoodDateEvidenceSet,
): FoodDateEvidenceSet => ({
  storageStartedOn: {
    source: 'household',
    certainty: evidence?.storageStartedOn.certainty ?? 'known',
  },
  purchasedOn: dates.purchasedOn == null
    ? null
    : { source: 'household', certainty: evidence?.purchasedOn?.certainty ?? 'known' },
  openedOn: dates.openedOn == null
    ? null
    : { source: 'household', certainty: evidence?.openedOn?.certainty ?? 'known' },
  useFirstOn: dates.useFirstOn == null
    ? null
    : { source: 'household', certainty: evidence?.useFirstOn?.certainty ?? 'known' },
  manufacturerDate: dates.manufacturerDate == null
    ? null
    : { source: 'packaging', certainty: evidence?.manufacturerDate?.certainty ?? 'known' },
});

const copyContainer = (container: FoodContainer): FoodContainer =>
  structuredClone(container);

const makeEvent = (
  eventType: InventoryEventType,
  before: FoodContainer | null,
  after: FoodContainer | null,
  summary: string,
  context: MutationContext,
  reason: string | null = null,
  revertsEventId: string | null = null,
): InventoryEvent => ({
  id: context.eventId,
  idempotencyKey: context.idempotencyKey,
  householdId: context.householdId,
  containerId: after?.id ?? before?.id ?? null,
  eventType,
  payload: { before, after, summary, reason },
  occurredAt: context.now,
  deviceId: context.deviceId,
  deviceSequence: context.deviceSequence,
  revertsEventId,
});

const mutate = (
  container: FoodContainer,
  updates: Partial<FoodContainer>,
  eventType: InventoryEventType,
  summary: string,
  context: MutationContext,
  reason: string | null = null,
): InventoryMutation => {
  const before = copyContainer(container);
  const after: FoodContainer = {
    ...copyContainer(container),
    ...updates,
    version: container.version + 1,
    updatedAt: context.now,
  };
  return {
    container: after,
    event: makeEvent(eventType, before, copyContainer(after), summary, context, reason),
  };
};

const assertPresent = (container: FoodContainer): void => {
  if (!['present', 'partial'].includes(container.status)) {
    throw new DomainError('Ce contenant n’est pas actuellement présent.', 'INVALID_STATE');
  }
};

export const calculateHorizonOn = (
  storageStartedOn: string,
  rule: HorizonRuleSnapshot | null,
): string | null => {
  if (rule?.durationDays == null) {
    return null;
  }
  const date = new Date(`${storageStartedOn}T12:00:00.000Z`);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  date.setUTCDate(date.getUTCDate() + rule.durationDays);
  return date.toISOString().slice(0, 10);
};

export const createDraftContainer = (
  id: string,
  assignment: VisibleNumberAssignment | null,
  input: CreateContainerInput,
  context: MutationContext,
): InventoryMutation => {
  const normalizedName = input.name.trim();
  const normalizedBarcode = input.barcode?.trim() || null;
  if (!normalizedName) {
    throw new DomainError('Le nom du contenant est obligatoire.', 'INVALID_STATE');
  }
  if (normalizedBarcode != null && !/^\d{8,14}$/u.test(normalizedBarcode)) {
    throw new DomainError('Le code-barres doit contenir entre 8 et 14 chiffres.', 'INVALID_STATE');
  }
  if (input.quantity != null && (!Number.isFinite(input.quantity) || input.quantity <= 0)) {
    throw new DomainError('La quantité doit être strictement positive.', 'INVALID_QUANTITY');
  }
  if ((input.quantity == null) !== (input.unit == null)) {
    throw new DomainError('La quantité et son unité doivent être renseignées ensemble.', 'INVALID_QUANTITY');
  }

  const numbered = assignment != null;
  const fridgeDates = {
    purchasedOn: input.purchasedOn ?? null,
    openedOn: input.openedOn ?? null,
    useFirstOn: input.useFirstOn ?? null,
  };
  assertFridgeDates(numbered ? 'visible-number' : 'internal-only', fridgeDates);
  const horizon = numbered
    ? input.qualityHorizonOn ?? calculateHorizonOn(input.storageStartedOn, input.horizonRule)
    : null;
  const container: FoodContainer = {
    id,
    householdId: context.householdId,
    trackingMode: numbered ? 'visible-number' : 'internal-only',
    serialNumber: assignment?.serialNumber ?? null,
    numberAssignedAt: assignment?.assignedAt ?? null,
    numberReleasedAt: null,
    numberAssignmentKind: assignment?.kind ?? null,
    numberPolicyVersion: numbered ? VISIBLE_NUMBER_QUARANTINE.version : null,
    rotationAlerts: [],
    name: normalizedName,
    barcode: normalizedBarcode,
    categoryId: input.categoryId,
    locationId: input.locationId,
    initialQuantity: input.quantity,
    remainingQuantity: input.quantity,
    unit: input.unit,
    storageStartedOn: input.storageStartedOn,
    dateEvidence: normalizeDateEvidence(
      {
        storageStartedOn: input.storageStartedOn,
        purchasedOn: numbered ? null : fridgeDates.purchasedOn,
        openedOn: numbered ? null : fridgeDates.openedOn,
        useFirstOn: numbered ? null : fridgeDates.useFirstOn,
        manufacturerDate: input.manufacturerDate,
      },
      input.dateEvidence,
    ),
    purchasedOn: numbered ? null : fridgeDates.purchasedOn,
    openedOn: numbered ? null : fridgeDates.openedOn,
    useFirstOn: numbered ? null : fridgeDates.useFirstOn,
    manufacturerDateType: input.manufacturerDateType,
    manufacturerDate: input.manufacturerDate,
    qualityHorizonOn: horizon,
    horizonRule:
      !numbered || input.horizonRule == null ? null : structuredClone(input.horizonRule),
    status: numbered ? 'draft' : 'present',
    incidentStatus: 'none',
    incidentNote: '',
    auditFlag: 'ok',
    notes: input.notes.trim(),
    version: 1,
    createdAt: context.now,
    updatedAt: context.now,
  };

  return {
    container,
    event: makeEvent(
      numbered ? 'created_draft' : 'confirmed',
      null,
      copyContainer(container),
      assignment?.kind === 'reused'
        ? `Numéro ${assignment.serialNumber} réutilisé — nouvel historique séparé`
        : assignment == null
          ? `${container.name} ajouté sans numéro visible`
          : `Numéro ${assignment.serialNumber} attribué`,
      context,
    ),
  };
};

export const confirmContainer = (
  container: FoodContainer,
  context: MutationContext,
): InventoryMutation => {
  if (!hasVisibleNumber(container)) {
    throw new DomainError('Cet aliment sans numéro est déjà enregistré.', 'INVALID_STATE');
  }
  if (container.status !== 'draft') {
    throw new DomainError('Ce numéro a déjà été confirmé.', 'INVALID_STATE');
  }
  return mutate(
    container,
    { status: 'present' },
    'confirmed',
    `N° ${container.serialNumber} confirmé sur le contenant`,
    context,
  );
};

export const finalizeReservedContainer = (
  container: FoodContainer,
  locationId: string,
  context: MutationContext,
): InventoryMutation => {
  if (!hasVisibleNumber(container)) {
    throw new DomainError('Cet aliment sans numéro est déjà enregistré.', 'INVALID_STATE');
  }
  if (container.status !== 'draft') {
    throw new DomainError('Ce numéro a déjà été confirmé.', 'INVALID_STATE');
  }
  if (!locationId.trim()) {
    throw new DomainError('Choisissez un appareil avant de confirmer.', 'INVALID_STATE');
  }
  return mutate(
    container,
    { status: 'present', locationId },
    'confirmed',
    `N° ${container.serialNumber} confirmé sur le contenant`,
    context,
  );
};

export const abandonReservedContainer = (
  container: FoodContainer,
  context: MutationContext,
): InventoryMutation => {
  if (!hasVisibleNumber(container)) {
    throw new DomainError('Un aliment sans numéro ne possède aucune réservation à abandonner.', 'INVALID_STATE');
  }
  if (container.status !== 'draft') {
    throw new DomainError('Seule une réservation en cours peut être abandonnée.', 'INVALID_STATE');
  }
  const releaseAt = calculateVisibleNumberReleaseAt(context.now);
  return mutate(
    container,
    {
      status: 'discarded',
      auditFlag: 'ok',
      numberReleasedAt: releaseAt,
    },
    'discarded',
    `Ajout abandonné — N° ${container.serialNumber} réservé 30 jours jusqu’au ${releaseAt.slice(0, 10)}`,
    context,
  );
};

export const consumeTotal = (
  container: FoodContainer,
  context: MutationContext,
): InventoryMutation => {
  assertPresent(container);
  const releaseAt = hasVisibleNumber(container)
    ? calculateVisibleNumberReleaseAt(context.now)
    : null;
  return mutate(
    container,
    {
      status: 'out',
      remainingQuantity: container.remainingQuantity == null ? null : 0,
      auditFlag: 'ok',
      numberReleasedAt: releaseAt,
    },
    'consumed_total',
    releaseAt == null
      ? `${container.name} entièrement consommé`
      : `N° ${container.serialNumber} entièrement sorti — numéro réservé 30 jours jusqu’au ${releaseAt.slice(0, 10)}`,
    context,
  );
};

export const consumePartial = (
  container: FoodContainer,
  input: PartialConsumption,
  context: MutationContext,
  reason: string | null = null,
): InventoryMutation => {
  assertPresent(container);
  if (container.remainingQuantity == null || container.unit == null) {
    throw new DomainError(
      'Ajoutez d’abord une quantité à la fiche pour enregistrer une sortie partielle.',
      'INVALID_QUANTITY',
    );
  }
  if (input.unit !== container.unit) {
    throw new DomainError('Le changement d’unité doit passer par une correction explicite.', 'UNIT_MISMATCH');
  }
  if (!Number.isFinite(input.value) || input.value < 0) {
    throw new DomainError('La quantité ne peut pas être négative.', 'INVALID_QUANTITY');
  }

  const current = container.remainingQuantity;
  const remaining = roundQuantity(input.mode === 'remaining' ? input.value : current - input.value);
  if (remaining < 0 || remaining > current) {
    throw new DomainError('La quantité restante doit être comprise entre zéro et le stock actuel.', 'INVALID_QUANTITY');
  }
  if (remaining === 0) {
    const releaseAt = hasVisibleNumber(container)
      ? calculateVisibleNumberReleaseAt(context.now)
      : null;
    return mutate(
      container,
      {
        status: 'out',
        remainingQuantity: 0,
        locationId: input.replacementLocationId,
        auditFlag: 'ok',
        numberReleasedAt: releaseAt,
      },
      'consumed_total',
      releaseAt == null
        ? `${container.name} entièrement utilisé`
        : `N° ${container.serialNumber} entièrement utilisé — numéro réservé 30 jours jusqu’au ${releaseAt.slice(0, 10)}`,
      context,
      reason,
    );
  }

  return mutate(
    container,
    {
      status: 'partial',
      remainingQuantity: remaining,
      locationId: input.replacementLocationId,
      auditFlag: 'ok',
    },
    'consumed_partial',
    `${containerReference(container)} : ${remaining} ${container.unit} restant`,
    context,
    reason,
  );
};

export const moveContainer = (
  container: FoodContainer,
  locationId: string,
  context: MutationContext,
): InventoryMutation => {
  assertPresent(container);
  if (locationId === container.locationId) {
    throw new DomainError('Choisissez un autre emplacement.', 'INVALID_STATE');
  }
  return mutate(
    container,
    { locationId, auditFlag: 'ok' },
    'moved',
    `${containerReference(container)} déplacé`,
    context,
  );
};

export const discardContainer = (
  container: FoodContainer,
  reason: string,
  context: MutationContext,
): InventoryMutation => {
  assertPresent(container);
  const releaseAt = hasVisibleNumber(container)
    ? calculateVisibleNumberReleaseAt(context.now)
    : null;
  return mutate(
    container,
    {
      status: 'discarded',
      remainingQuantity: container.remainingQuantity == null ? null : 0,
      auditFlag: 'ok',
      numberReleasedAt: releaseAt,
    },
    'discarded',
    releaseAt == null
      ? `${container.name} signalé jeté ou perdu`
      : `N° ${container.serialNumber} signalé jeté ou perdu — numéro réservé 30 jours jusqu’au ${releaseAt.slice(0, 10)}`,
    context,
    reason.trim() || null,
  );
};

export const restoreContainer = (
  container: FoodContainer,
  locationId: string,
  quantity: number | null,
  assignment: VisibleNumberAssignment | null,
  context: MutationContext,
): InventoryMutation => {
  if (!['out', 'discarded'].includes(container.status)) {
    throw new DomainError('Seul un contenant sorti ou jeté peut être restauré.', 'INVALID_STATE');
  }
  if (quantity != null && quantity <= 0) {
    throw new DomainError('La quantité restaurée doit être positive.', 'INVALID_QUANTITY');
  }
  const remainingQuantity = container.unit == null ? null : (quantity ?? container.initialQuantity);
  if (hasVisibleNumber(container) && assignment == null) {
    throw new DomainError('Le numéro visible doit être restauré avec le contenant.', 'INVALID_STATE');
  }
  if (!hasVisibleNumber(container) && assignment != null) {
    throw new DomainError('Un aliment stocké sans numéro ne peut pas recevoir un numéro visible.', 'INVALID_STATE');
  }
  return mutate(
    container,
    {
      serialNumber: assignment?.serialNumber ?? null,
      numberAssignedAt: assignment?.assignedAt ?? null,
      numberReleasedAt: null,
      numberAssignmentKind: assignment?.kind ?? null,
      numberPolicyVersion: assignment == null ? null : VISIBLE_NUMBER_QUARANTINE.version,
      status:
        remainingQuantity != null &&
        container.initialQuantity != null &&
        remainingQuantity < container.initialQuantity
          ? 'partial'
          : 'present',
      locationId,
      remainingQuantity,
      auditFlag: 'to_confirm',
    },
    'restored',
    assignment?.kind === 'reassigned'
      ? `Le n° ${container.serialNumber} avait été réattribué — contenant restauré sous le n° ${assignment.serialNumber}`
      : assignment == null
        ? `${container.name} restauré sans numéro visible`
        : `N° ${assignment.serialNumber} restauré dans l’inventaire`,
    context,
  );
};

export const correctContainer = (
  container: FoodContainer,
  updates: Partial<
    Pick<
      FoodContainer,
      | 'name'
      | 'barcode'
      | 'categoryId'
      | 'locationId'
      | 'remainingQuantity'
      | 'unit'
      | 'storageStartedOn'
      | 'dateEvidence'
      | 'purchasedOn'
      | 'openedOn'
      | 'useFirstOn'
      | 'manufacturerDateType'
      | 'manufacturerDate'
      | 'qualityHorizonOn'
      | 'horizonRule'
      | 'notes'
    >
  >,
  context: MutationContext,
): InventoryMutation => {
  if (updates.remainingQuantity != null && updates.remainingQuantity < 0) {
    throw new DomainError('La quantité ne peut pas être négative.', 'INVALID_QUANTITY');
  }
  if (updates.name != null && !updates.name.trim()) {
    throw new DomainError('Le nom ne peut pas être vide.', 'INVALID_STATE');
  }
  const normalizedBarcode = updates.barcode?.trim() || null;
  if ('barcode' in updates && normalizedBarcode != null && !/^\d{8,14}$/u.test(normalizedBarcode)) {
    throw new DomainError('Le code-barres doit contenir entre 8 et 14 chiffres.', 'INVALID_STATE');
  }
  assertFridgeDates(container.trackingMode, {
    purchasedOn: 'purchasedOn' in updates ? updates.purchasedOn : container.purchasedOn ?? null,
    openedOn: 'openedOn' in updates ? updates.openedOn : container.openedOn ?? null,
    useFirstOn: 'useFirstOn' in updates ? updates.useFirstOn : container.useFirstOn ?? null,
  });
  const nextDates = {
    storageStartedOn: updates.storageStartedOn ?? container.storageStartedOn,
    purchasedOn: 'purchasedOn' in updates ? updates.purchasedOn : container.purchasedOn ?? null,
    openedOn: 'openedOn' in updates ? updates.openedOn : container.openedOn ?? null,
    useFirstOn: 'useFirstOn' in updates ? updates.useFirstOn : container.useFirstOn ?? null,
    manufacturerDate: 'manufacturerDate' in updates ? updates.manufacturerDate : container.manufacturerDate,
  };
  return mutate(
    container,
    {
      ...updates,
      name: updates.name?.trim() ?? container.name,
      barcode: 'barcode' in updates ? normalizedBarcode : container.barcode ?? null,
      dateEvidence: normalizeDateEvidence(nextDates, updates.dateEvidence ?? container.dateEvidence),
      notes: updates.notes?.trim() ?? container.notes,
    },
    'corrected',
    `Fiche de ${containerReference(container)} corrigée`,
    context,
  );
};

export const markAuditMissing = (
  container: FoodContainer,
  context: MutationContext,
): InventoryMutation => {
  assertPresent(container);
  if (!hasVisibleNumber(container)) {
    throw new DomainError('L’audit par numéro est réservé aux congélateurs.', 'INVALID_STATE');
  }
  return mutate(
    container,
    { auditFlag: 'missing' },
    'audit_missing',
    `N° ${container.serialNumber} absent lors de l’audit`,
    context,
  );
};

export const confirmAuditPresent = (
  container: FoodContainer,
  context: MutationContext,
): InventoryMutation => {
  if (!hasVisibleNumber(container)) {
    throw new DomainError('L’audit par numéro est réservé aux congélateurs.', 'INVALID_STATE');
  }
  return mutate(
    container,
    { auditFlag: 'ok' },
    'audit_confirmed',
    `Présence du n° ${container.serialNumber} confirmée`,
    context,
  );
};

export const undoEvent = (
  current: FoodContainer,
  eventToUndo: InventoryEvent,
  context: MutationContext,
  assignment: VisibleNumberAssignment | null = null,
): InventoryMutation => {
  const previous = eventToUndo.payload.before;
  const eventAfter = eventToUndo.payload.after;
  if (previous == null || eventAfter == null || eventToUndo.containerId !== current.id) {
    throw new DomainError('Cette action ne peut pas être annulée.', 'STALE_UNDO');
  }
  if (current.version !== eventAfter.version) {
    throw new DomainError(
      'La fiche a changé depuis cette action. Utilisez une correction explicite.',
      'STALE_UNDO',
    );
  }
  const restored: FoodContainer = {
    ...copyContainer(previous),
    ...(assignment == null
      ? {}
      : {
          serialNumber: assignment.serialNumber,
          numberAssignedAt: assignment.assignedAt,
          numberReleasedAt: null,
          numberAssignmentKind: assignment.kind,
          numberPolicyVersion: VISIBLE_NUMBER_QUARANTINE.version,
        }),
    version: current.version + 1,
    updatedAt: context.now,
  };
  const summary =
    assignment?.kind === 'reassigned'
      ? `Annulation : le n° ${previous.serialNumber} avait été réattribué, ce contenant porte maintenant le n° ${assignment.serialNumber}`
      : assignment?.kind === 'restored'
        ? `Annulation : n° ${assignment.serialNumber} restauré`
        : `Annulation : ${eventToUndo.payload.summary}`;
  return {
    container: restored,
    event: makeEvent(
      'undo',
      copyContainer(current),
      copyContainer(restored),
      summary,
      context,
      null,
      eventToUndo.id,
    ),
  };
};
